import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router";

import { useAuth } from "../../app/auth";
import {
  type Action,
  type Category,
  type Comparison,
  type Discrepancy,
  type DiscrepancyDetail,
  type DiscrepancyStatus,
  type Resolution,
  comparisonDetail,
  decide,
  discrepancyDetail,
  packComparisons,
  runComparison,
  shown,
  tracePath,
} from "./profileClient";
import { useLatestRequest } from "./latest";

const CATEGORIES: Category[] = [
  "type",
  "requiredness",
  "persistence",
  "domain",
  "relationship",
  "version",
  "add_on",
  "custom",
];
const RESOLUTIONS: Resolution[] = [
  "environment_intended",
  "pack_correction_proposed",
  "profile_recapture_required",
  "not_material",
];
const ACTIONS: Record<DiscrepancyStatus, [Action, ...Action[]]> = {
  open: ["acknowledge", "resolve"],
  acknowledged: ["resolve", "reopen"],
  resolved: ["reopen"],
};
const POPULATIONS = [
  ["pack_elements", "Pack elements"],
  ["compared", "Compared"],
  ["consistent", "Consistent"],
  ["differing", "Differing"],
  ["missing_in_profile", "Missing from the profile"],
  ["not_comparable", "Not comparable"],
  ["not_applicable", "Not applicable"],
  ["not_covered_by_pack", "Not covered by the pack"],
] as const;

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

function ComparisonSummary({ comparison }: { comparison: Comparison }) {
  const sections = Object.entries(comparison.summary.sections);
  const reasons = sections.filter(([, p]) => p.not_comparable_reason);
  return (
    <>
      <p>
        {comparison.profile_version} compared with Maximo Base Pack {comparison.pack_key}{" "}
        {comparison.pack_version} (ruleset {comparison.ruleset_version}) at{" "}
        {comparison.compared_at}: {comparison.summary.discrepancies} discrepancies.
      </p>
      <table className="grid">
        <caption>Every element accounted for</caption>
        <thead>
          <tr>
            <th>Section</th>
            {POPULATIONS.map(([key, label]) => (
              <th key={key}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sections.map(([section, p]) => (
            <tr key={section}>
              <th scope="row">{section}</th>
              {POPULATIONS.map(([key]) => (
                <td key={key}>{p[key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {reasons.length > 0 && (
        <ul aria-label="Why some pack elements could not be compared">
          {reasons.map(([section, p]) => (
            <li key={section}>
              {section}: {p.not_comparable_reason}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function DecisionForm({
  discrepancy,
  onDecided,
}: {
  discrepancy: Discrepancy;
  onDecided: () => Promise<void>;
}) {
  const actions = ACTIONS[discrepancy.status];
  const [action, setAction] = useState<Action>(actions[0]);
  const [resolution, setResolution] = useState<Resolution>("environment_intended");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    try {
      await decide(discrepancy.id, {
        action,
        resolution: action === "resolve" ? resolution : undefined,
        reason: reason.trim(),
      });
      setReason("");
      await onDecided();
    } catch (caught) {
      setError(message(caught));
    }
  }

  return (
    <form onSubmit={submit} aria-label="Decide this discrepancy">
      <label>
        Decision{" "}
        <select value={action} onChange={(event) => setAction(event.target.value as Action)}>
          {actions.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </label>{" "}
      {action === "resolve" && (
        <label>
          Resolution{" "}
          <select
            value={resolution}
            onChange={(event) => setResolution(event.target.value as Resolution)}
          >
            {RESOLUTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
      )}{" "}
      <label>
        Reason <input value={reason} onChange={(event) => setReason(event.target.value)} />
      </label>{" "}
      <button type="submit" disabled={!reason.trim()}>
        Record decision
      </button>
      {error && <p role="alert">{error}</p>}
    </form>
  );
}

function DiscrepancyDetails({
  detail,
  canDecide,
  onDecided,
}: {
  detail: DiscrepancyDetail;
  canDecide: boolean;
  onDecided: () => Promise<void>;
}) {
  const d = detail.discrepancy;
  return (
    <section aria-label={`Discrepancy ${d.element} ${d.difference}`}>
      <h3>
        {d.element}: {d.difference}
      </h3>
      <p>{d.message}</p>
      <p>
        Status: {d.status}
        {d.resolution ? ` (${d.resolution})` : ""}. The environment profile governs; the Base Pack
        value is a generic reference only.
      </p>
      {canDecide ? (
        <DecisionForm key={`${d.id}-${d.status}`} discrepancy={d} onDecided={onDecided} />
      ) : (
        <p>Only an engagement lead or a data mapper can decide discrepancies.</p>
      )}
      <table className="grid">
        <caption>History of this difference</caption>
        <thead>
          <tr>
            <th>Profile version</th>
            <th>Pack version</th>
            <th>Status</th>
            <th>Decisions</th>
          </tr>
        </thead>
        <tbody>
          {detail.history.map((entry) => (
            <tr key={entry.discrepancy.id}>
              <td>{entry.profile_version}</td>
              <td>{entry.pack_version}</td>
              <td>{entry.discrepancy.status}</td>
              <td>
                <ol>
                  {entry.decisions.map((decision) => (
                    <li key={decision.id}>
                      {decision.action}
                      {decision.resolution ? ` (${decision.resolution})` : ""} by{" "}
                      {decision.decided_by} as {decision.acting_role} at {decision.decided_at}:{" "}
                      {decision.reason}
                    </li>
                  ))}
                </ol>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Differences between generic Maximo product knowledge (the Base Pack) and this profile version
 * (DESIGN-SPEC 19.4). The profile always governs; differences are findings for review. */
export function DiscrepancyPanel({ snapshotId }: { snapshotId: string }) {
  const { hasPermission } = useAuth();
  const canCompare = hasPermission("engagement.manage");
  const canDecide = canCompare || hasPermission("mapping.certify");
  const [comparison, setComparison] = useState<Comparison | null>(null);
  const [compared, setCompared] = useState<boolean | null>(null);
  const [category, setCategory] = useState<Category | "">("");
  const [found, setFound] = useState<Discrepancy[]>([]);
  const [detail, setDetail] = useState<DiscrepancyDetail | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const beginList = useLatestRequest();
  const beginDetail = useLatestRequest();

  const load = useCallback(
    async (wanted: Category | "") => {
      const current = beginList();
      setError(null);
      try {
        const [latest] = await packComparisons(snapshotId);
        if (!current()) return;
        setCompared(latest !== undefined);
        if (latest === undefined) return;
        const result = await comparisonDetail(latest.id, { category: wanted || undefined });
        if (!current()) return;
        setComparison(result.comparison);
        setFound(result.discrepancies);
      } catch (caught) {
        if (current()) setError(message(caught));
      }
    },
    [snapshotId, beginList],
  );

  useEffect(() => {
    void load("");
  }, [load]);

  async function compare(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    try {
      await runComparison(snapshotId, reason.trim());
      await load(category);
    } catch (caught) {
      setError(message(caught));
    }
  }

  async function open(discrepancyId: string) {
    const current = beginDetail();
    setError(null);
    try {
      const found = await discrepancyDetail(discrepancyId);
      if (current()) setDetail(found);
    } catch (caught) {
      if (current()) setError(message(caught));
    }
  }

  async function refresh() {
    if (detail) await open(detail.discrepancy.id);
    await load(category);
  }

  return (
    <div>
      {error && <p role="alert">{error}</p>}
      {compared === false && (
        <>
          <p>This profile version has not been compared with a Maximo Base Pack.</p>
          {canCompare && (
            <form onSubmit={compare} aria-label="Compare with the Base Pack">
              <label>
                Reason <input value={reason} onChange={(event) => setReason(event.target.value)} />
              </label>{" "}
              <button type="submit" disabled={!reason.trim()}>
                Compare with the Base Pack
              </button>
            </form>
          )}
        </>
      )}
      {comparison && (
        <>
          <ComparisonSummary comparison={comparison} />
          <label>
            Category{" "}
            <select
              value={category}
              onChange={(event) => {
                const next = event.target.value as Category | "";
                setCategory(next);
                void load(next);
              }}
            >
              <option value="">all</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <table className="grid">
            <caption>Discrepancies</caption>
            <thead>
              <tr>
                <th>Category</th>
                <th>Difference</th>
                <th>Element</th>
                <th>Environment profile (governs)</th>
                <th>Base Pack (reference)</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {found.map((d) => {
                const [objectname = "", attributename = ""] = d.element.split(".");
                return (
                  <tr key={d.id}>
                    <td>{d.category}</td>
                    <td>{d.difference}</td>
                    <td>
                      {d.element_kind === "attribute" ? (
                        <Link to={tracePath(snapshotId, objectname, attributename)}>
                          {d.element}
                        </Link>
                      ) : (
                        d.element
                      )}
                    </td>
                    <td>{shown(d.profile_value)}</td>
                    <td>{shown(d.pack_value)}</td>
                    <td>
                      {d.status}
                      {d.resolution ? ` (${d.resolution})` : ""}
                      {d.prior ? ` — earlier: ${d.prior.resolution ?? d.prior.status}` : ""}
                    </td>
                    <td>
                      <button type="button" onClick={() => void open(d.id)}>
                        Details of {d.element} {d.difference}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
      {detail && <DiscrepancyDetails detail={detail} canDecide={canDecide} onDecided={refresh} />}
    </div>
  );
}
